import * as Dialog from '@radix-ui/react-dialog'
import { CSS } from '@stitches/react/types/css-util'
import React, { createContext, PropsWithChildren, useState } from 'react'
import { keyframes, styled } from '../../../../lib/stitches'
import { IconButton } from '../../../core/Components'
import { CloseIcon } from '../../../core/icons'
import { gray2, gray8 } from '../../../platform/color'
import useUploadModal, { Step } from './useUploadModal'

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 }
})

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
})

const Overlay = styled(Dialog.Overlay, {
  backgroundColor: 'rgba(0,0,0,.5)',
  position: 'fixed',
  inset: 0,
  animation: `${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
  zIndex: 5000
})

const Content = styled(Dialog.Content, {
  position: 'fixed',
  top: '50%',
  left: '50%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  transform: 'translate(-50%, -50%)',
  outline: 'none',
  borderRadius: '16px',
  fontFamily: 'DM Sans',
  color: gray8,
  background: gray2,
  maxWidth: '520px',
  width: '100%',
  padding: '50px',
  animation: `${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`
})

interface IUploadContext {
  title: string
  steps: Step[]
  stepIndex: number
  customArtistAddress: string
  setCustomArtistAddress: (string) => void
  stepBack: () => void
  stepForward: () => void
}

export const UploadModalContext = createContext<Partial<IUploadContext>>({
  steps: [],
  stepIndex: 0,
  customArtistAddress: undefined
})

interface Props {
  title: string
  steps: Step[]
  trigger: React.ReactNode
  handleOpen?: () => void
  contentCSS?: CSS
}

const UploadModal: React.FC<PropsWithChildren<Props>> = ({
  handleOpen,
  trigger,
  steps,
  children,
  title,
  contentCSS
}) => {
  const { stepIndex, setStepIndex, handleStepBack, handleStepForward } = useUploadModal(steps)

  const [customArtistAddress, setCustomArtistAddress] = useState<string>()

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && handleOpen) {
      handleOpen()
    } else if (!isOpen) {
      setStepIndex(0)
    }
  }

  const isCloseHidden = steps[stepIndex]?.hideClose

  return (
    <UploadModalContext.Provider
      value={{
        title,
        steps,
        stepIndex,
        stepBack: handleStepBack,
        stepForward: handleStepForward,
        customArtistAddress,
        setCustomArtistAddress
      }}
    >
      <Dialog.Root onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Overlay>
            <Content css={contentCSS}>
              {!isCloseHidden && (
                <Dialog.Close asChild>
                  <IconButton css={{ position: 'absolute', right: '20px', top: '20px' }}>
                    <CloseIcon style={{ color: 'black', width: '20px', height: '20px' }} />
                  </IconButton>
                </Dialog.Close>
              )}
              {children}
            </Content>
          </Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </UploadModalContext.Provider>
  )
}

export default UploadModal
